import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';

/** The payload emitted when the user submits the new-feedback form. */
export interface NewFeedbackSubmission {
  element: string;
  url: string;
  observation: string;
  recommendation: string;
  /** 'mobile' | 'tablet' | 'desktop'. */
  screen: string;
  /** The chosen screenshot file, or null when none was attached. */
  screenshot: File | null;
}

/** A screen option for the screen select. */
interface ScreenOption {
  value: string;
  label: string;
}

// Accepted screenshot types, surfaced in the drop-zone copy.
const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
// A presigned single PUT to S3 caps an object at 5 GB; anything larger needs a
// multipart upload, which this flow doesn't implement.
const MAX_UPLOAD_BYTES = 5 * 1024 * 1024 * 1024;

/**
 * Right-hand slide-over drawer for creating a new feedback item. Presentational:
 * it owns the form state and a local screenshot preview, but does NOT talk to the
 * API. On submit it emits the form values plus the chosen `File`; the host
 * orchestrates the signed-URL upload and the create request, toggling
 * `submitting` while that runs.
 *
 * @example
 * <fb-new-feedback
 *   [open]="newFeedbackOpen()"
 *   [submitting]="creatingFeedback()"
 *   (submit)="onSubmitNewFeedback($event)"
 *   (close)="newFeedbackOpen.set(false)" />
 */
@Component({
  selector: 'fb-new-feedback',
  templateUrl: './new-feedback.html',
  styleUrl: './new-feedback.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '(document:keydown.escape)': 'onClose()' },
})
export class NewFeedbackDrawer {
  /** Whether the drawer is shown. */
  readonly open = input<boolean>(false);

  /** When true, the host is uploading/creating; the form is locked. */
  readonly submitting = input<boolean>(false);

  /** Emitted with the form values + screenshot file when the user submits. */
  readonly submit = output<NewFeedbackSubmission>();

  /** Emitted when the user closes the drawer (button, overlay, or Escape). */
  readonly close = output<void>();

  protected readonly screenOptions: ScreenOption[] = [
    { value: 'desktop', label: 'Desktop' },
    { value: 'mobile', label: 'Mobile' },
    { value: 'tablet', label: 'Tablet' },
  ];

  protected readonly acceptAttr = ACCEPTED_TYPES.join(',');

  // Form fields.
  protected readonly element = signal('');
  protected readonly url = signal('');
  protected readonly observation = signal('');
  protected readonly recommendation = signal('');
  protected readonly screen = signal('');

  // Screenshot state.
  protected readonly screenshotFile = signal<File | null>(null);
  protected readonly screenshotPreview = signal<string | null>(null);
  protected readonly screenshotError = signal<string | null>(null);
  protected readonly dragOver = signal(false);

  /** The required fields must be filled before the form can be submitted. */
  protected readonly valid = computed(
    () =>
      !!this.element().trim() &&
      !!this.url().trim() &&
      !!this.screen() &&
      !!this.observation().trim(),
  );

  constructor() {
    // Start each opening from a clean slate. The reset itself runs untracked so
    // the effect only depends on `open()` — otherwise it would re-subscribe to
    // (and fight with) the very signals it resets, e.g. the screenshot preview.
    effect(() => {
      if (this.open()) {
        untracked(() => this.resetForm());
      }
    });
  }

  /** Updates a text field from an input/textarea event. */
  protected setField(
    field: 'element' | 'url' | 'observation' | 'recommendation' | 'screen',
    event: Event,
  ): void {
    const value = (event.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement).value;
    this[field].set(value);
  }

  protected onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.acceptFile(input.files?.[0] ?? null);
    // Allow re-selecting the same file after a remove.
    input.value = '';
  }

  protected onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragOver.set(false);
    this.acceptFile(event.dataTransfer?.files?.[0] ?? null);
  }

  protected onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.dragOver.set(true);
  }

  protected onDragLeave(): void {
    this.dragOver.set(false);
  }

  /** Validates a chosen file and sets it as the screenshot (with a preview). */
  private acceptFile(file: File | null): void {
    if (!file) {
      return;
    }
    if (!ACCEPTED_TYPES.includes(file.type)) {
      this.screenshotError.set('Unsupported file type. Use PNG, JPG, WEBP, or GIF.');
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      this.screenshotError.set('That file exceeds the 5 GB upload limit.');
      return;
    }
    this.clearPreview();
    this.screenshotError.set(null);
    this.screenshotFile.set(file);
    this.screenshotPreview.set(URL.createObjectURL(file));
  }

  protected removeScreenshot(): void {
    this.clearPreview();
    this.screenshotFile.set(null);
    this.screenshotError.set(null);
  }

  protected onSubmit(): void {
    if (!this.valid() || this.submitting()) {
      return;
    }
    this.submit.emit({
      element: this.element().trim(),
      url: this.url().trim(),
      observation: this.observation().trim(),
      recommendation: this.recommendation().trim(),
      screen: this.screen(),
      screenshot: this.screenshotFile(),
    });
  }

  protected onClose(): void {
    if (this.submitting()) {
      return;
    }
    this.close.emit();
  }

  private resetForm(): void {
    this.element.set('');
    this.url.set('');
    this.observation.set('');
    this.recommendation.set('');
    this.screen.set('');
    this.removeScreenshot();
  }

  /** Revokes the current preview object URL to avoid a memory leak. */
  private clearPreview(): void {
    const preview = this.screenshotPreview();
    if (preview) {
      URL.revokeObjectURL(preview);
      this.screenshotPreview.set(null);
    }
  }
}
